import React, { useState,useEffect } from "react";
import { retrieveUploadedFile } from "../../api/settingsLogApi";
import {
  Collapse,
  Upload,
  ColorPicker,
  Input,
  InputNumber,
  Typography,
  Slider,
  Radio, // used for label alignment
  Switch,
  Select,
  Button,
  message,
} from "antd";
import {
  AppstoreOutlined,
  BgColorsOutlined,
  TagOutlined,
  PlusOutlined,
  SettingOutlined,
  CloudUploadOutlined,
  CloseCircleFilled,
} from "@ant-design/icons";
import "./CustomizePanel.css";
import "./utm-styles.css";

const { Panel } = Collapse;
const { Text } = Typography;

const ERROR_CORRECTION_OPTIONS = [
  { value: "low", label: "L", name: "Low", tooltip: "Low — 7% recovery" },
  {
    value: "medium",
    label: "M",
    name: "Medium",
    tooltip: "Medium — 15% recovery",
  },
  {
    value: "quartile",
    label: "Q",
    name: "Quartile",
    tooltip: "Quartile — 25% recovery",
  },
  {
    value: "high",
    label: "H",
    name: "High",
    tooltip: "High — 30% recovery (recommended with logo)",
  },
];

const ALIGNMENT_OPTIONS = [
  { value: "left", label: "Left", desc: "Align label to the left" },
  { value: "center", label: "Center", desc: "Center the label" },
  { value: "right", label: "Right", desc: "Align label to the right" },
];

const ROUND_BLOCK_OPTIONS = [
  {
    value: "margin",
    label: "Margin",
    desc: "Absorbs rounding into the quiet zone. Final image size stays exact.",
  },
  {
    value: "enlarge",
    label: "Enlarge",
    desc: "QR code and image enlarge slightly when rounding up.",
  },
  {
    value: "shrink",
    label: "Shrink",
    desc: "QR code and image shrink slightly when rounding down.",
  },
];

const EC_RECOVERY = { low: 7, medium: 15, quartile: 25, high: 30 };

/**
 * Accordion customization panel.
 * All options map 1-to-1 with the endroid/qr-code PHP library parameters.
 *
 * Sections:
 *  1. QR Settings  — size, margin, errorCorrectionLevel, roundBlockSizeMode
 *  2. Colors       — foregroundColor, backgroundColor
 *  3. Logo         — logo file, logoResizeToWidth, logoPunchoutBackground
 *  4. Label        — labelText, labelAlignment, labelFontSize, labelColor
 */
const CustomizePanel = ({
  size,
  margin,
  errorCorrectionLevel,
  roundBlockSizeMode,
  foregroundColor,
  backgroundColor,
  logo,
  logoId,
  logoResizeToWidth,
  logoPunchoutBackground,
  labelText,
  labelAlignment,
  labelFontSize,
  labelColor,
  utmSource,
  utmMedium,
  utmCampaign,
  utmContent,
  utmTerm,
  onFieldChange,
  onUploadLogo,
  uploadedFiles,
  logoInfo
}) => {
  const [fileList, setFileList] = useState([]);
  const [activeKey, setActiveKey] = useState("colors");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [selectedLogo, setSelectedLogo] = useState(null);

useEffect(() => {
  if (!logoInfo?.id) return;

  const loadLogo = async () => {
    try {
      setSelectedFileId(logoInfo.id);

      const data = await retrieveUploadedFile(logoInfo.id);
      setSelectedLogo(data.file);

      onFieldChange("logoId", logoInfo.id);

      if (logoInfo.resizeWidth) {
        onFieldChange("logoResizeToWidth", logoInfo.resizeWidth);
      }
    } catch (err) {
      console.error(err);
    }
  };

  loadLogo();
}, [
  logoInfo?.id,
  logoInfo?.resizeWidth,
  onFieldChange,
]);

  const handleLogoChange = ({ file, fileList: newFileList }) => {
    // File removed
    if (file.status === "removed" || newFileList.length === 0) {
      setFileList([]);
      setUploadError(null);
      onFieldChange("logo", null);
      return;
    }
    const raw = file.originFileObj || file;
    if (raw) {
      // 2 MB size limit
      if (raw.size > 2 * 1024 * 1024) {
        setUploadError("Logo must be 2 MB or smaller.");
        return;
      }
      const thumbUrl = URL.createObjectURL(raw);
      // Inject thumbUrl + force status "done" so the picture-card renders the preview
      const entry = {
        uid: file.uid,
        name: file.name,
        status: "done",
        thumbUrl,
        originFileObj: raw,
      };
      setFileList([entry]);
      setUploadError(null);
      onFieldChange("logo", raw);
    }
  };

  const handleUploadClick = async () => {
    if (!logo) return;
    setUploading(true);
    setUploadError(null);
    try {
      await onUploadLogo(logo);
       // Clear existing selected logo
    setSelectedFileId(undefined);
    setSelectedLogo(null);
      message.success("Logo uploaded successfully!");
    } catch (err) {
      setUploadError(err.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleExistingLogoSelect = async (fileId) => {
    try {
      setSelectedFileId(fileId);

      const data = await retrieveUploadedFile(fileId);
      const file = data.file;

      setSelectedLogo(file);

      // Store selected logo id
      onFieldChange("logoId", fileId);

      message.success("Logo selected successfully");
    } catch (err) {
      console.error(err);
      message.error(err.message || "Unable to load logo");
    }
  };

  const beforeUpload = () => false;

  return (
    <Collapse
      className="customize-collapse"
      expandIconPosition="end"
      accordion={true}
      activeKey={activeKey ? [activeKey] : []}
      onChange={(keys) => {
        // In accordion mode, keys is either a single string or empty array
        const key = Array.isArray(keys) ? keys[0] : keys;
        setActiveKey(key ?? null);
      }}
    >
      {/* ─────────────── 1. QR SETTINGS ─────────────── */}
      <Panel
        key="settings"
        header={
          <span className="panel-header">
            <SettingOutlined className="panel-icon" />
            QR Settings
          </span>
        }
      >
        <div className="settings-grid">
          {/* Size */}
          <div className="setting-row">
            <Text className="setting-label">Size (px)</Text>
            <Text className="setting-desc">
              Output image dimensions in pixels (100–1000 px)
            </Text>
            <div className="slider-row">
              <Slider
                min={100}
                max={1000}
                step={10}
                value={size}
                onChange={(v) => onFieldChange("size", v)}
                className="setting-slider"
              />
              <InputNumber
                min={100}
                max={1000}
                step={10}
                value={size}
                onChange={(v) => onFieldChange("size", v ?? 300)}
                className="setting-number"
                size="small"
              />
            </div>
          </div>

          {/* Margin */}
          <div className="setting-row">
            <Text className="setting-label">Margin (px)</Text>
            <Text className="setting-desc">
              White-space border around the QR code (0–50 px)
            </Text>
            <div className="slider-row">
              <Slider
                min={0}
                max={50}
                value={margin}
                onChange={(v) => onFieldChange("margin", v)}
                className="setting-slider"
              />
              <InputNumber
                min={0}
                max={50}
                value={margin}
                onChange={(v) => onFieldChange("margin", v ?? 10)}
                className="setting-number"
                size="small"
              />
            </div>
          </div>

          {/* Error Correction Level */}
          <div className="setting-row">
            <Text className="setting-label">Error Correction Level</Text>
            <Text className="setting-desc">
              Higher levels keep the QR scannable even when partially covered —
              use H when adding a logo.
            </Text>
            <Radio.Group
              value={errorCorrectionLevel}
              onChange={(e) =>
                onFieldChange("errorCorrectionLevel", e.target.value)
              }
              optionType="button"
              buttonStyle="solid"
              size="middle"
              className="ec-radio-group"
            >
              {ERROR_CORRECTION_OPTIONS.map((opt) => (
                <Radio.Button key={opt.value} value={opt.value}>
                  {opt.name}
                </Radio.Button>
              ))}
            </Radio.Group>
            <div className="ec-info-card">
              <div className="ec-bar-row">
                <span className="ec-bar-label">Recovery capacity</span>
                <span className="ec-bar-pct">
                  {EC_RECOVERY[errorCorrectionLevel]}%
                </span>
              </div>
              <div className="ec-bar-track">
                <div
                  className="ec-bar-fill"
                  style={{
                    width: `${(EC_RECOVERY[errorCorrectionLevel] / 30) * 100}%`,
                  }}
                />
              </div>
              <span className="ec-info-text">
                {
                  ERROR_CORRECTION_OPTIONS.find(
                    (o) => o.value === errorCorrectionLevel,
                  )?.tooltip
                }
              </span>
            </div>
          </div>

          {/* Round Block Size Mode */}
          <div className="setting-row">
            <Text className="setting-label">Block Rounding Mode</Text>
            <Text className="setting-desc">
              Controls how pixel blocks are rounded to keep the QR code sharp
              and scannable.
            </Text>
            <Radio.Group
              value={roundBlockSizeMode}
              onChange={(e) =>
                onFieldChange("roundBlockSizeMode", e.target.value)
              }
              optionType="button"
              buttonStyle="solid"
              size="middle"
              className="round-radio-group"
            >
              {ROUND_BLOCK_OPTIONS.map((opt) => (
                <Radio.Button key={opt.value} value={opt.value}>
                  {opt.label}
                  {opt.badge && (
                    <span className="radio-button-badge">{opt.badge}</span>
                  )}
                </Radio.Button>
              ))}
            </Radio.Group>
            <div className="round-info-card">
              <span className="round-info-title">
                {ROUND_BLOCK_OPTIONS.find((o) => o.value === roundBlockSizeMode)
                  ?.label || "Margin"}
              </span>
              <span className="round-info-text">
                {
                  ROUND_BLOCK_OPTIONS.find(
                    (o) => o.value === roundBlockSizeMode,
                  )?.desc
                }
              </span>
            </div>
          </div>
        </div>
      </Panel>

      {/* ─────────────── 2. COLORS ─────────────── */}
      <Panel
        key="colors"
        header={
          <span className="panel-header">
            <BgColorsOutlined className="panel-icon" />
            Colors
          </span>
        }
      >
        <Text className="color-section-desc">
          Keep strong contrast between foreground and background. Light
          backgrounds improve readability on most scanners.
        </Text>
        <div className="color-row">
          <div className="color-item">
            <Text className="color-label">Foreground</Text>
            <ColorPicker
              value={foregroundColor}
              onChange={(_, hex) => onFieldChange("foregroundColor", hex)}
              format="hex"
              disabledFormat
              showText
              size="large"
            />
          </div>
          <div className="color-item">
            <Text className="color-label">Background</Text>
            <ColorPicker
              value={backgroundColor}
              onChange={(_, hex) => onFieldChange("backgroundColor", hex)}
              format="hex"
              disabledFormat
              showText
              size="large"
            />
          </div>
        </div>
      </Panel>

      {/* ─────────────── 3. LABEL ─────────────── */}
      <Panel
        key="label"
        header={
          <span className="panel-header">
            <TagOutlined className="panel-icon" />
            Label
            <span className="panel-optional">optional</span>
          </span>
        }
      >
        <div className="label-section">
          <Input
            className="label-input"
            placeholder="e.g. Scan to visit our website"
            value={labelText}
            onChange={(e) => onFieldChange("labelText", e.target.value)}
            maxLength={80}
            showCount
          />

          {/* Alignment */}
          <div className="setting-row">
            <Text className="setting-label">Alignment</Text>
            <Radio.Group
              value={labelAlignment}
              onChange={(e) => onFieldChange("labelAlignment", e.target.value)}
              optionType="button"
              buttonStyle="solid"
              size="middle"
              className="alignment-radio-group"
            >
              {ALIGNMENT_OPTIONS.map((opt) => (
                <Radio.Button key={opt.value} value={opt.value}>
                  {opt.label}
                </Radio.Button>
              ))}
            </Radio.Group>
            <div className="alignment-info-card">
              <span className="alignment-info-title">
                {ALIGNMENT_OPTIONS.find((o) => o.value === labelAlignment)
                  ?.label || "Left"}
              </span>
              <span className="alignment-info-text">
                {
                  ALIGNMENT_OPTIONS.find((o) => o.value === labelAlignment)
                    ?.desc
                }
              </span>
            </div>
          </div>

          {/* Font size */}
          <div className="setting-row">
            <Text className="setting-label">Font Size (px)</Text>
            <div className="slider-row">
              <Slider
                min={10}
                max={40}
                value={labelFontSize}
                onChange={(v) => onFieldChange("labelFontSize", v)}
                className="setting-slider"
                disabled={!labelText}
              />
              <InputNumber
                min={10}
                max={40}
                value={labelFontSize}
                onChange={(v) => onFieldChange("labelFontSize", v ?? 20)}
                className="setting-number"
                size="small"
                disabled={!labelText}
              />
            </div>
          </div>

          {/* Label color */}
          <div className="color-row">
            <div className="color-item">
              <Text className="color-label">Text Color</Text>
              <ColorPicker
                value={labelColor}
                onChange={(_, hex) => onFieldChange("labelColor", hex)}
                format="hex"
                disabledFormat
                showText
                size="large"
                disabled={!labelText}
              />
            </div>
          </div>
        </div>
      </Panel>

      {/* ─────────────── 4. LOGO ─────────────── */}
      <Panel
        key="logo"
        header={
          <span className="panel-header">
            <AppstoreOutlined className="panel-icon" />
            Logo
            <span className="panel-optional">optional</span>
          </span>
        }
      >
        <div className="logo-section">
          <div style={{ marginBottom: 16 }}>
            <Text strong>Select Existing Logo</Text>

            <Select
              style={{ width: "100%", marginTop: 8 }}
              placeholder="Choose a logo"
              value={selectedFileId}
              onChange={handleExistingLogoSelect}
              options={uploadedFiles?.map((file) => ({
                value: file.id,
                label: file.name,
              }))}
            />
          </div>
          <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
            {/* Left Side */}
            <div style={{ flex: 1 }}>
              <Upload
                listType="picture-card"
                maxCount={1}
                beforeUpload={beforeUpload}
                onChange={handleLogoChange}
                fileList={fileList}
                accept=".png,.jpg,.jpeg,.webp"
                className="logo-upload"
              >
                {fileList.length === 0 && (
                  <div className="upload-placeholder">
                    <PlusOutlined />
                    <span>Select</span>
                  </div>
                )}
              </Upload>
              <Text className="upload-hint">
                PNG with transparent background recommended · Max 2 MB · Upload
                the logo before generating
              </Text>

              {/* Upload button — shown once a file is selected but not yet uploaded */}
              {logo && !logoId && (
                <Button
                  type="primary"
                  icon={<CloudUploadOutlined />}
                  loading={uploading}
                  onClick={handleUploadClick}
                  disabled={uploading}
                  className="logo-upload-btn"
                  block
                >
                  {uploading ? "Uploading…" : "Upload Logo"}
                </Button>
              )}

              {/* Upload error */}
              {uploadError && (
                <div className="logo-upload-error">
                  <CloseCircleFilled />
                  &nbsp;{uploadError}
                </div>
              )}
            </div>

            {/* Right Side */}
            {selectedLogo && (
              <div
                style={{
                  width: 150,
                  textAlign: "center",
                  border: "1px solid #d9d9d9",
                  borderRadius: 8,
                  padding: 10,
                }}
              >
                <img
                  src={selectedLogo.url}
                  alt={selectedLogo.name}
                  style={{
                    width: 100,
                    height: 100,
                    objectFit: "contain",
                  }}
                />
              </div>
            )}
          </div>

          {/* File selector */}

          {/* Logo resize width */}
          <div className="setting-row">
            <Text className="setting-label">Logo Width (px)</Text>
            <Text className="setting-desc">
              The logo is resized to this width inside the QR code.
            </Text>
            <div className="slider-row">
              <Slider
                min={20}
                max={200}
                value={logoResizeToWidth}
                onChange={(v) => onFieldChange("logoResizeToWidth", v)}
                className="setting-slider"
                disabled={!logoId}
              />
              <InputNumber
                min={20}
                max={200}
                value={logoResizeToWidth}
                onChange={(v) => onFieldChange("logoResizeToWidth", v ?? 50)}
                className="setting-number"
                size="small"
                disabled={!logoId}
              />
            </div>
          </div>
        </div>
      </Panel>

      {/* ─────────────── 5. UTM PARAMETERS ─────────────── */}
      <Panel
        key="utm"
        header={
          <span className="panel-header">
            <SettingOutlined className="panel-icon" />
            UTM Parameters
            <span className="panel-optional">optional</span>
          </span>
        }
      >
        <div className="utm-section">
          <Text className="utm-section-desc">
            Add UTM parameters to track QR code campaign performance in
            analytics.
          </Text>
          <div className="utm-grid">
            <div className="utm-field">
              <Text className="setting-label">Source</Text>
              <Input
                placeholder="e.g. qr"
                value={utmSource}
                onChange={(e) => onFieldChange("utmSource", e.target.value)}
                maxLength={100}
              />
            </div>
            <div className="utm-field">
              <Text className="setting-label">Medium</Text>
              <Input
                placeholder="e.g. code"
                value={utmMedium}
                onChange={(e) => onFieldChange("utmMedium", e.target.value)}
                maxLength={100}
              />
            </div>
            <div className="utm-field">
              <Text className="setting-label">Campaign</Text>
              <Input
                placeholder="e.g. spring-2024"
                value={utmCampaign}
                onChange={(e) => onFieldChange("utmCampaign", e.target.value)}
                maxLength={100}
              />
            </div>
            <div className="utm-field">
              <Text className="setting-label">Content</Text>
              <Input
                placeholder="e.g. product-launch"
                value={utmContent}
                onChange={(e) => onFieldChange("utmContent", e.target.value)}
                maxLength={100}
              />
            </div>
            <div className="utm-field">
              <Text className="setting-label">Term</Text>
              <Input
                placeholder="e.g. qr-code"
                value={utmTerm}
                onChange={(e) => onFieldChange("utmTerm", e.target.value)}
                maxLength={100}
              />
            </div>
          </div>
        </div>
      </Panel>
    </Collapse>
  );
};

export default CustomizePanel;
